import type { Time } from "./Time";

import { EmptyTime } from "./EmptyTime";
import { PerformanceEntry } from "perf_hooks";

export class PerformanceTime extends EmptyTime implements Time {
  private readonly startLabel: string | undefined;
  private readonly measureLabel: string;

  constructor(measureLabel: string, startLabel?: string) {
    super();
    this.measureLabel = measureLabel;
    this.startLabel = startLabel;
  }

  get markPerfEntries(): PerformanceEntryList {
    if (!this.startLabel) {
      return [];
    }
    return performance.getEntriesByName(this.startLabel);
  }

  get measurePerfEntries(): PerformanceEntryList {
    return performance.getEntriesByName(this.measureLabel);
  }

  get countMarks(): number {
    return this.markPerfEntries?.length || 0;
  }

  get countMeasures(): number {
    return this.measurePerfEntries?.length || 0;
  }

  reset() {
    if (this.startLabel) {
      performance.mark(this.startLabel);
    }
  }

  measure(): number | void {
    if (this.countMarks > 0) {
      performance.measure(this.measureLabel, this.startLabel);
      const performanceEntry: PerformanceEntry = performance
        .getEntriesByName(this.measureLabel)
        .pop() as PerformanceEntry;
      const duration: number = performanceEntry.duration || 0;
      return parseFloat(`${duration}`);
    }
  }

  measureOnce(startTime: number, endTime: number = new Date().getTime()): void {
    if (this.countMeasures == 0 && !!startTime) {
      try {
        performance.measure(this.measureLabel, {
          start: startTime,
          end: endTime,
        });
      } catch (e) {
        /*       */
      }
    }
  }

  get timeTotal(): number {
    /*              */
    if (this.countMeasures > 0) {
      return Math.round(
        this.measurePerfEntries
          .map((e) => e.duration)
          .reduce((prev: number, current: number): number => prev + current, 0),
      );
    }
    return -1;
  }

  get timeAverage(): number {
    if (this.countMeasures > 0) {
      return Math.round(this.timeSum / this.countMeasures);
    }
    return -1;
  }

  get timeSum(): number {
    if (this.countMeasures > 0) {
      return this.measurePerfEntries.reduce((e, n) => e + n.duration, 0);
    }
    return -1;
  }

  get navigationStart(): number {
    return performance.timing.navigationStart;
  }
}
